import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MessageSquare,
  Sparkles,
  Copy,
  Check,
  Trash2,
  Send,
  Loader2,
  FileText,
  MessageCircle,
  Inbox,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AUDIENCE_OPTIONS = [
  { value: 'parent', label: 'Parent — warm recap' },
  { value: 'whatsapp', label: 'WhatsApp — short & friendly' },
  { value: 'team', label: 'MDT colleague — clinical' },
];

export default function ParentTouchpointsCard({ child, embedded = false }) {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isTherapist = user?.role === 'therapist';
  const childId = child?.id;

  const [notes, setNotes] = useState([]);
  const [selectedNoteIds, setSelectedNoteIds] = useState([]);
  const [audience, setAudience] = useState('parent');
  const [generating, setGenerating] = useState(false);
  const [summaryDialog, setSummaryDialog] = useState({ open: false, text: '', meta: null });
  const [copiedSummary, setCopiedSummary] = useState(false);

  const [feedbackItems, setFeedbackItems] = useState([]);
  const [composer, setComposer] = useState({ title: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [recapSent, setRecapSent] = useState(false);
  const [showMultiDialog, setShowMultiDialog] = useState(false);

  const fetchNotes = useCallback(async () => {
    if (!childId || !isTherapist) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/progress/${childId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Only finalised (non-draft) notes are eligible.
      const eligible = (res.data || [])
        .filter((n) => !n.is_draft)
        .slice(0, 30);
      setNotes(eligible);
    } catch (e) {
      // Silent — section just won't render notes.
    }
  }, [childId, isTherapist]);

  const fetchFeedback = useCallback(async () => {
    if (!childId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/general-feedback/${childId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeedbackItems(res.data || []);
    } catch (e) {
      // Silent
    }
  }, [childId]);

  useEffect(() => {
    fetchNotes();
    fetchFeedback();
  }, [fetchNotes, fetchFeedback]);

  const toggleNote = (noteId) => {
    setSelectedNoteIds((prev) =>
      prev.includes(noteId) ? prev.filter((n) => n !== noteId) : [...prev, noteId],
    );
  };

  const handleGenerate = async () => {
    if (selectedNoteIds.length === 0) {
      toast.error('Select at least one session note');
      return;
    }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/progress/multi-summary`,
        { child_id: childId, note_ids: selectedNoteIds, audience },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSummaryDialog({
        open: true,
        text: res.data.summary || '',
        meta: {
          session_count: res.data.session_count,
          date_range: res.data.date_range,
          audience: res.data.audience,
          fallback: res.data.fallback,
        },
      });
      setRecapSent(false);
      setShowMultiDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summaryDialog.text);
      setCopiedSummary(true);
      toast.success('Copied — paste it into WhatsApp or your EMR.');
      setTimeout(() => setCopiedSummary(false), 2000);
    } catch {
      toast.error('Copy failed');
    }
  };

  // Sends the (possibly edited) AI-recap as a general-feedback message so the
  // parent sees it on PlayFirst exactly like a regular post-session update,
  // but spanning multiple sessions. We auto-fill a friendly title with the
  // date range to make it clear it's a recap.
  const sendRecapToParent = async () => {
    const text = (summaryDialog.text || '').trim();
    if (!text) {
      toast.error('Nothing to send — generate a recap first.');
      return;
    }
    setSendingRecap(true);
    try {
      const token = localStorage.getItem('token');
      const meta = summaryDialog.meta || {};
      const range = meta.date_range || {};
      const sessions = meta.session_count;
      const title =
        range.start && range.end && range.start !== range.end
          ? `Recap from ${range.start} → ${range.end}`
          : sessions
            ? `Recap of ${sessions} recent session${sessions === 1 ? '' : 's'}`
            : 'Weekly recap';
      await axios.post(
        `${API}/general-feedback`,
        { child_id: childId, title, message: text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRecapSent(true);
      toast.success("Sent to parent — they'll see it on PlayFirst.");
      fetchFeedback();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingRecap(false);
    }
  };

  const sendFeedback = async () => {
    if (!composer.message.trim()) {
      toast.error('Write a message first');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/general-feedback`,
        {
          child_id: childId,
          title: composer.title.trim() || null,
          message: composer.message.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Sent to parent. They\'ll see it on their dashboard.');
      setComposer({ title: '', message: '' });
      fetchFeedback();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm('Delete this update? Parents who already saw it will still have a notification.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/general-feedback/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Deleted');
      fetchFeedback();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const markRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/general-feedback/${id}/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      fetchFeedback();
    } catch (e) {
      // silent
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Card
      className={
        embedded
          ? 'bg-transparent border-0 shadow-none p-0'
          : 'p-6 bg-white rounded-3xl border border-stone-100 mb-8'
      }
      data-testid="parent-touchpoints-card"
    >
      {!embedded && (
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h3 className="text-xl font-serif text-primary-900">Parent Touchpoints</h3>
            <p className="text-sm text-stone-500">
              {isTherapist
                ? `Send updates and AI-written recaps to ${child?.name}'s family.`
                : `Messages from your therapist about ${child?.name}.`}
            </p>
          </div>
        </div>
      )}

      {/* Multi-SOAP summary — therapist only */}
      {isTherapist && notes.length > 0 && (
        <div className="mb-5 p-4 rounded-2xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-700" />
              <div>
                <p className="font-medium text-purple-900">Multi-session AI recap</p>
                <p className="text-xs text-purple-700">
                  Pick 2+ session notes and let AI write a holistic update you can copy/paste.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowMultiDialog(true)}
              className="rounded-full bg-purple-600 hover:bg-purple-700"
              data-testid="open-multi-summary-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Build recap
            </Button>
          </div>
        </div>
      )}

      {/* Composer — therapist only */}
      {isTherapist && (
        <div className="mb-5 p-4 rounded-2xl border border-stone-200 bg-stone-50">
          <p className="text-sm font-medium text-stone-800 mb-3">Send a quick update</p>
          <div className="space-y-2">
            <Input
              placeholder="Title (optional) — e.g. 'Holiday schedule'"
              value={composer.title}
              onChange={(e) => setComposer({ ...composer, title: e.target.value })}
              className="h-10 rounded-xl"
              data-testid="general-feedback-title-input"
            />
            <Textarea
              placeholder="Type your message to the parent…"
              value={composer.message}
              onChange={(e) => setComposer({ ...composer, message: e.target.value })}
              className="rounded-xl min-h-24"
              data-testid="general-feedback-message-input"
            />
            <div className="flex justify-end">
              <Button
                onClick={sendFeedback}
                disabled={sending}
                className="rounded-full"
                data-testid="send-general-feedback-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send to parent
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History list — both roles */}
      <div className="space-y-2">
        {feedbackItems.length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-3">
            {isTherapist
              ? 'No general updates yet — send your first one above.'
              : 'No updates from your therapist yet.'}
          </p>
        ) : (
          feedbackItems.map((fb) => {
            const isUnreadForParent =
              !isTherapist && user?.id && !(fb.read_by || []).includes(user.id);
            return (
              <div
                key={fb.id}
                className={`p-3 rounded-xl border ${
                  isUnreadForParent
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-white border-stone-100'
                }`}
                data-testid={`general-feedback-item-${fb.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {fb.title && (
                      <p className="text-sm font-semibold text-stone-900 mb-0.5">
                        {fb.title}
                      </p>
                    )}
                    <p className="text-sm text-stone-700 whitespace-pre-wrap">{fb.message}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-stone-400">
                      <span>{formatDate(fb.created_at)}</span>
                      {isTherapist && (
                        <Badge variant="secondary" className="text-[10px]">
                          {(fb.read_by || []).length} read
                        </Badge>
                      )}
                      {isUnreadForParent && (
                        <Badge className="text-[10px] bg-amber-200 text-amber-900">New</Badge>
                      )}
                    </div>
                  </div>
                  {isTherapist && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteFeedback(fb.id)}
                      className="rounded-full h-8 w-8 p-0 text-red-500"
                      data-testid={`delete-general-feedback-${fb.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  {isUnreadForParent && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => markRead(fb.id)}
                      className="rounded-full"
                      data-testid={`mark-general-feedback-read-${fb.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Mark read
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Multi-SOAP picker dialog */}
      <Dialog open={showMultiDialog} onOpenChange={setShowMultiDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Build multi-session recap</DialogTitle>
            <DialogDescription>
              Pick the sessions you want to summarise. We'll never invent details — the AI only
              uses what you've documented.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Audience</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger
                  className="mt-1 h-10 rounded-xl"
                  data-testid="multi-summary-audience-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border border-stone-200 rounded-2xl max-h-72 overflow-y-auto divide-y divide-stone-100">
              {notes.map((n) => {
                const checked = selectedNoteIds.includes(n.id);
                const dateStr = n.date ? formatDate(n.date) : '—';
                const snippet = (n.objective || n.parent_summary || n.observation || '')
                  .toString()
                  .trim()
                  .slice(0, 100);
                return (
                  <label
                    key={n.id}
                    className={`flex items-start gap-3 p-3 cursor-pointer ${
                      checked ? 'bg-purple-50' : 'hover:bg-stone-50'
                    }`}
                    data-testid={`multi-summary-note-${n.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleNote(n.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm font-medium text-stone-800">
                        <FileText className="w-3.5 h-3.5 text-stone-400" />
                        {dateStr}
                      </div>
                      {snippet && (
                        <p className="text-xs text-stone-500 line-clamp-2 mt-0.5">{snippet}</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-stone-500">
              {selectedNoteIds.length} session{selectedNoteIds.length === 1 ? '' : 's'} selected
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowMultiDialog(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || selectedNoteIds.length === 0}
              className="rounded-full"
              data-testid="generate-multi-summary-btn"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate recap
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary output dialog */}
      <Dialog
        open={summaryDialog.open}
        onOpenChange={(o) => setSummaryDialog((s) => ({ ...s, open: o }))}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {summaryDialog.meta?.audience === 'whatsapp'
                ? 'WhatsApp-ready recap'
                : summaryDialog.meta?.audience === 'team'
                  ? 'Clinical recap'
                  : 'Parent recap'}
            </DialogTitle>
            <DialogDescription>
              {summaryDialog.meta?.session_count} session
              {summaryDialog.meta?.session_count === 1 ? '' : 's'}
              {summaryDialog.meta?.date_range?.start &&
                ` from ${summaryDialog.meta.date_range.start} → ${summaryDialog.meta.date_range.end}`}
              {summaryDialog.meta?.fallback && ' • offline fallback'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={summaryDialog.text}
            onChange={(e) => setSummaryDialog((s) => ({ ...s, text: e.target.value }))}
            className="rounded-2xl min-h-48"
            data-testid="multi-summary-output"
          />
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setSummaryDialog({ open: false, text: '', meta: null })}
              className="rounded-full"
            >
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                // wa.me without a phone number opens WhatsApp's contact picker
                // so the therapist can choose the parent's chat. Safer than
                // guessing the parent's phone format/country code.
                const url = `https://wa.me/?text=${encodeURIComponent(summaryDialog.text || '')}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
              className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              data-testid="send-via-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Send via WhatsApp
            </Button>
            <Button
              onClick={sendRecapToParent}
              disabled={sendingRecap || recapSent}
              className="rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-70"
              data-testid="send-recap-to-parent-btn"
            >
              {sendingRecap ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : recapSent ? (
                <Check className="w-4 h-4 mr-2" />
              ) : (
                <Inbox className="w-4 h-4 mr-2" />
              )}
              {recapSent ? 'Sent on PlayFirst' : 'Send to parent on PlayFirst'}
            </Button>
            <Button
              onClick={async () => {
                await copySummary();
              }}
              className="rounded-full"
              data-testid="copy-multi-summary-btn"
            >
              {copiedSummary ? (
                <Check className="w-4 h-4 mr-2" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              {copiedSummary ? 'Copied' : 'Copy to clipboard'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
